import { FC, useEffect, useState } from 'react';
import { useActionFormContext } from '../common/ActionForm';
import { MaxAmountInput } from '../common/MaxAmountInput';
import { MaxAmount, getDisplayedMax, serializeAmount } from '../../../hooks/useMaxTokenAmount';
import { asDecimal, isNull } from '@silo/utils';
import { LINKS } from '@silo/types';
import { DescriptionExternalLink, InfoIcon, StaticDescription, TooltipDescription } from '@silo/ui';

export const RepayMaxAmount: FC = () => {
  const {
    normalizedTokenAmount,
    selectedAsset: { userBalance, decimals, projectedDebtBalance, symbol },
    setTokenAmount,
    isPending,
    isFullAmount,
  } = useActionFormContext();
  const [isPartialRepay, setIsPartialRepay] = useState(false);

  const userBalanceMax: MaxAmount = { type: 'userBalance', value: { bigNumber: userBalance, decimals } };
  const protocolBalanceMax: MaxAmount = {
    type: 'protocolBalance',
    value: { bigNumber: projectedDebtBalance, decimals },
  };

  const effectiveMax: MaxAmount = userBalance.lt(projectedDebtBalance) ? userBalanceMax : protocolBalanceMax;

  const doMax = () => {
    if (effectiveMax.type === 'userBalance') {
      setIsPartialRepay(true);
    }

    const displayedMax = getDisplayedMax(effectiveMax);

    setTokenAmount(serializeAmount(displayedMax));
  };

  useEffect(() => {
    if (!isPartialRepay) return;

    if (isFullAmount) {
      setIsPartialRepay(false);
      return;
    }

    if (isNull(normalizedTokenAmount)) {
      setIsPartialRepay(false);
      return;
    }

    if (!normalizedTokenAmount.eq(effectiveMax.value.bigNumber)) {
      setIsPartialRepay(false);
      return;
    }
  }, [isPartialRepay, normalizedTokenAmount, effectiveMax.value, isFullAmount]);

  return (
    <div className="flex flex-col gap-5">
      {isFullAmount && <FullAmountWarning />}

      {!isFullAmount && isPartialRepay && (
        <p className="text-white ">Your position will be partially repaid due to insufficient balance</p>
      )}

      <MaxAmountInput
        max={protocolBalanceMax}
        doMax={doMax}
        maxLabelSlot={
          <div className="flex items-center gap-1.5">
            <span>Amount owed</span>
          </div>
        }
        maxButtonContentSlot={userBalance.lt(projectedDebtBalance) ? 'Max available' : undefined}
        disabled={isPending}
        symbol={symbol}
      />

      <div className="flex justify-between">
        <div className="text-shale-200">Wallet balance</div>
        <div className={`font-mono font-medium ${isPending ? 'text-slate-400' : 'text-white'}`}>
          {asDecimal({ bigNumber: userBalance, decimals })} {symbol}
        </div>
      </div>
    </div>
  );
};

const FullAmountWarning: FC = () => {
  const description = (
    <div className="flex flex-col gap-2.5">
      <p>
        If you enter an amount to repay that is within $1 of your total loan, your position will be automatically
        repaid.
      </p>

      <p>To account for accruing interest, the amount shown may be slightly higher than the amount actually repaid.</p>

      <DescriptionExternalLink href={LINKS.SILOPEDIA.FULL_REPAYMENT} />
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-white">
        <p>Your position will be fully repaid.</p>

        <TooltipDescription trigger={<InfoIcon className="w-4 h-4 text-white" />}>{description}</TooltipDescription>
      </div>

      <StaticDescription>{description}</StaticDescription>
    </div>
  );
};
